import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, User, CreditCard, ShoppingBag, Package, Award, FileText, MapPin, History, Save, Loader2, Trash2, AlertTriangle } from "lucide-react";

/**
 * Per-user drill-down for customer support.
 *
 * Loads the entire linked-record set for a given user and lets an admin edit
 * profile + membership fields inline. EVERY write requires a "reason for
 * change" note that gets appended to admin_audit_log so support activity is
 * fully traceable.
 *
 * Route: /admin/users/:userId
 */
export default function AdminSupportView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Local edit state — dirty diff vs the server view.
  const [userPatch, setUserPatch] = useState({});
  const [membershipPatch, setMembershipPatch] = useState({});
  const [reason, setReason] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/admin/users/${userId}/support-view`);
      setData(data);
      setUserPatch({});
      setMembershipPatch({});
      setReason("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load user");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const setUserField = (k, v) => setUserPatch(p => ({ ...p, [k]: v }));
  const setMembershipField = (k, v) => setMembershipPatch(p => ({ ...p, [k]: v }));

  const hasChanges = Object.keys(userPatch).length > 0 || Object.keys(membershipPatch).length > 0;

  const save = async () => {
    if (!hasChanges) return;
    if (reason.trim().length < 6) {
      setToast({ kind: "err", msg: "Please explain the reason for this change (min 6 chars) before saving." });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}/support-edit`, {
        reason_for_change: reason.trim(),
        user_patch: Object.keys(userPatch).length ? userPatch : null,
        membership_patch: Object.keys(membershipPatch).length ? membershipPatch : null,
      });
      setToast({ kind: "ok", msg: "Saved. Audit log updated." });
      await load();
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Save failed" });
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const doDelete = async () => {
    if (deleteReason.trim().length < 6) {
      setToast({ kind: "err", msg: "Please give a reason (min 6 chars) for deleting this user." });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setDeleteBusy(true);
    try {
      // axios needs `data` for DELETE bodies
      await api.delete(`/admin/users/${userId}`, { data: { reason_for_change: deleteReason.trim() } });
      setToast({ kind: "ok", msg: "User deleted. Returning to admin…" });
      setTimeout(() => navigate("/admin"), 1200);
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Delete failed" });
      setDeleteBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (err) return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="support-view-error">
      <Link to="/admin" className="text-sm text-[var(--ff-primary)] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back to admin</Link>
      <h1 className="font-display text-4xl mt-6">{err}</h1>
    </div>
  );
  if (!data) return <div className="p-12 text-center text-[var(--ff-text-muted)]" data-testid="support-view-loading">Loading…</div>;

  const u = data.user;
  const m = data.membership;

  const valFor = (k, base) => (k in userPatch ? userPatch[k] : (base?.[k] ?? ""));
  const memVal = (k) => (k in membershipPatch ? membershipPatch[k] : (m?.[k] ?? ""));

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid={`support-view-${u.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="text-sm text-[var(--ff-primary)] inline-flex items-center gap-1 hover:underline" data-testid="support-back">
            <ArrowLeft size={14}/> Back to admin
          </Link>
          <h1 className="font-display text-4xl mt-3 leading-tight" data-testid="support-user-name">{u.name || u.email}</h1>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">
            <span className="uppercase tracking-wider">{u.role}</span>
            {u.email_verified ? " · verified" : " · unverified"}
            {u.is_founding_member && " · founding"}
            {u.is_ambassador && " · ambassador"}
            {u.deleted_at && <span className="text-rose-700 font-semibold"> · DELETED {u.deleted_at.slice(0,10)}</span>}
          </p>
        </div>
        {!u.deleted_at && u.role !== "admin" && u.role !== "super_admin" && (
          <button
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 transition-colors px-4 py-2 text-sm font-medium"
            data-testid="support-delete-btn"
          >
            <Trash2 size={14}/> Delete user…
          </button>
        )}
      </div>

      {/* Change reason (required for any save) */}
      <div className="card-flat p-4 sm:p-5 border-2 border-[var(--ff-citrus)]" data-testid="support-reason-card">
        <label className="overline text-[var(--ff-citrus)]" htmlFor="reason-input">
          Reason for change (required)
        </label>
        <textarea
          id="reason-input"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. Customer support ticket #4831 — user requested phone number update after new SIM"
          className="input-base w-full mt-2 text-sm"
          data-testid="support-reason-input"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-[var(--ff-text-muted)]">
            Every save writes to <code>admin_audit_log</code> with a before / after snapshot.
          </p>
          <button
            onClick={save}
            disabled={!hasChanges || busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="support-save-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            Save {hasChanges ? `(${Object.keys(userPatch).length + Object.keys(membershipPatch).length} change${(Object.keys(userPatch).length + Object.keys(membershipPatch).length) > 1 ? "s" : ""})` : ""}
          </button>
        </div>
      </div>

      {/* Profile card */}
      <Section icon={<User size={16}/>} title="Profile" testid="support-profile">
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Name" value={valFor("name", u)} onChange={v => setUserField("name", v)} testid="edit-name"/>
          <TextField label="Email" value={valFor("email", u)} onChange={v => setUserField("email", v)} testid="edit-email"/>
          <TextField label="Phone" value={valFor("phone", u)} onChange={v => setUserField("phone", v)} testid="edit-phone"/>
          <SelectField label="Role" value={valFor("role", u)} onChange={v => setUserField("role", v)} testid="edit-role" options={[{v:"customer",l:"Customer"},{v:"vendor",l:"Vendor"}]}/>
          <ToggleField label="Email verified" value={!!valFor("email_verified", u)} onChange={v => setUserField("email_verified", v)} testid="edit-email-verified"/>
          <ToggleField label="Founding member" value={!!valFor("is_founding_member", u)} onChange={v => setUserField("is_founding_member", v)} testid="edit-founding"/>
          <ToggleField label="Ambassador" value={!!valFor("is_ambassador", u)} onChange={v => setUserField("is_ambassador", v)} testid="edit-ambassador"/>
          <NumberField label="Free-shipping credits" value={Number(valFor("free_shipping_credits", u)) || 0} onChange={v => setUserField("free_shipping_credits", v)} testid="edit-free-ship"/>
        </div>
      </Section>

      {/* Membership card */}
      <Section icon={<CreditCard size={16}/>} title="Membership" testid="support-membership">
        {!m ? (
          <p className="text-sm text-[var(--ff-text-muted)]">No membership on file.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Status" value={memVal("status") || m.status} onChange={v => setMembershipField("status", v)} testid="edit-mem-status"
              options={[{v:"active",l:"Active"},{v:"trial",l:"Trial"},{v:"cancelled",l:"Cancelled"},{v:"past_due",l:"Past due"},{v:"expired",l:"Expired"}]}/>
            <TextField label="Plan ID" value={memVal("plan_id") || m.plan_id || ""} onChange={v => setMembershipField("plan_id", v)} testid="edit-mem-plan"/>
            <TextField label="Current period end (ISO)" value={memVal("current_period_end") || m.current_period_end || ""} onChange={v => setMembershipField("current_period_end", v)} testid="edit-mem-period"/>
            <ReadRow label="Stripe subscription" value={m.stripe_subscription_id || "—"}/>
            <ReadRow label="Stripe customer" value={m.stripe_customer_id || "—"}/>
          </div>
        )}
      </Section>

      {/* Orders */}
      <Section icon={<ShoppingBag size={16}/>} title={`Orders (${data.orders.length})`} testid="support-orders">
        <RecordList
          rows={data.orders}
          empty="No orders yet."
          render={o => (
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium truncate">#{(o.id || "").slice(0,8)}</p>
                <p className="text-xs text-[var(--ff-text-muted)]">{o.created_at?.slice(0,10)} · {o.status}</p>
              </div>
              <span className="tabular-nums font-semibold">${Number(o.total || 0).toFixed(2)}</span>
            </div>
          )}
        />
      </Section>

      {/* Drop reservations */}
      <Section icon={<Package size={16}/>} title={`Drop reservations (${data.drop_reservations.length})`} testid="support-reservations">
        <RecordList
          rows={data.drop_reservations}
          empty="No drop reservations."
          render={r => (
            <div className="flex items-center justify-between gap-3 text-sm">
              <div>
                <p className="font-medium">{r.drop_slug}</p>
                <p className="text-xs text-[var(--ff-text-muted)]">{r.created_at?.slice(0,10)} · {r.status} · qty {r.quantity}</p>
              </div>
            </div>
          )}
        />
      </Section>

      {/* Addresses */}
      <Section icon={<MapPin size={16}/>} title={`Addresses (${data.addresses.length})`} testid="support-addresses">
        <RecordList
          rows={data.addresses}
          empty="No addresses on file."
          render={a => (
            <div className="text-sm">
              <p className="font-medium">{a.line1} {a.line2 || ""}</p>
              <p className="text-xs text-[var(--ff-text-muted)]">{a.city}, {a.state} {a.postal_code}</p>
            </div>
          )}
        />
      </Section>

      {/* Ambassador */}
      {data.ambassador && (
        <Section icon={<Award size={16}/>} title="Ambassador" testid="support-ambassador">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <ReadRow label="Referral code" value={data.ambassador.referral_code || "—"}/>
            <ReadRow label="Stripe promo ID" value={data.ambassador.stripe_promo_id || "—"}/>
            <ReadRow label="Commission rate" value={`${((data.ambassador.commission_rate || 0) * 100).toFixed(1)}%`}/>
            <ReadRow label="Total earned" value={`$${Number(data.ambassador.total_commission_earned || 0).toFixed(2)}`}/>
          </div>
          <div className="mt-4">
            <p className="overline">Recent commissions ({data.commissions.length})</p>
            <RecordList
              rows={data.commissions.slice(0, 8)}
              empty="No commissions yet."
              render={c => (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-xs text-[var(--ff-text-muted)]">{c.created_at?.slice(0,10)}</span>
                  <span className="tabular-nums">${Number(c.amount || 0).toFixed(2)} · {c.status}</span>
                </div>
              )}
            />
          </div>
        </Section>
      )}

      {/* Brand partner app */}
      {data.brand_partner_application && (
        <Section icon={<FileText size={16}/>} title="Brand-partner application" testid="support-brand-app">
          <div className="text-sm">
            <ReadRow label="Status" value={data.brand_partner_application.status}/>
            <ReadRow label="Brand" value={data.brand_partner_application.brand_name}/>
            <ReadRow label="Submitted" value={data.brand_partner_application.created_at?.slice(0,10)}/>
          </div>
        </Section>
      )}

      {/* Audit log */}
      <Section icon={<History size={16}/>} title={`Audit log (${data.audit_log.length})`} testid="support-audit-log">
        {data.audit_log.length === 0 ? (
          <p className="text-sm text-[var(--ff-text-muted)]">No admin changes recorded for this user yet.</p>
        ) : (
          <ul className="divide-y divide-[var(--ff-border)]">
            {data.audit_log.map(a => (
              <li key={a.id} className="py-3 text-sm" data-testid={`audit-row-${a.id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-medium">
                    {a.action} <span className="text-xs text-[var(--ff-text-muted)]">by {a.admin_email}</span>
                  </p>
                  <span className="text-xs text-[var(--ff-text-muted)] whitespace-nowrap">{a.created_at?.slice(0,16).replace("T", " ")}</span>
                </div>
                <p className="text-xs text-[var(--ff-text-secondary)] mt-1">&ldquo;{a.reason}&rdquo;</p>
                <div className="mt-2 grid sm:grid-cols-2 gap-2 text-[11px] text-[var(--ff-text-muted)]">
                  <div className="bg-red-50 rounded p-2"><span className="uppercase text-[9px] tracking-wider text-red-700">Before</span><pre className="whitespace-pre-wrap break-words mt-1 text-red-900">{JSON.stringify(a.before || {}, null, 0)}</pre></div>
                  <div className="bg-emerald-50 rounded p-2"><span className="uppercase text-[9px] tracking-wider text-emerald-700">After</span><pre className="whitespace-pre-wrap break-words mt-1 text-emerald-900">{JSON.stringify(a.after || {}, null, 0)}</pre></div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border px-4 py-3 shadow-lg ${toast.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="support-toast">
          {toast.msg}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !deleteBusy && setShowDelete(false)}
          data-testid="delete-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                <AlertTriangle size={20}/>
              </span>
              <div>
                <h3 className="font-display text-2xl">Delete this user?</h3>
                <p className="text-xs text-[var(--ff-text-muted)]">Soft delete · records preserved</p>
              </div>
            </div>
            <p className="text-sm text-[var(--ff-text-secondary)] leading-relaxed">
              This anonymizes <strong>{u.email}</strong>&rsquo;s profile (email, name, phone), revokes
              their active membership, empties their cart, and deactivates their ambassador row
              if any. Orders and commission history are <em>kept intact</em> for accounting.
              This cannot be undone from the UI.
            </p>
            <label className="block mt-4">
              <span className="overline text-rose-700">Reason for deletion (required)</span>
              <textarea
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                rows={3}
                placeholder="e.g. GDPR / CCPA delete request per ticket #4831"
                className="input-base w-full mt-2 text-sm"
                data-testid="delete-reason-input"
              />
            </label>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDelete(false)}
                disabled={deleteBusy}
                className="text-sm text-[var(--ff-text-secondary)] hover:text-[var(--ff-text-primary)] px-4 py-2"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={deleteBusy || deleteReason.trim().length < 6}
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="delete-confirm-btn"
              >
                {deleteBusy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                Confirm delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Section({ icon, title, children, testid }) {
  return (
    <section className="card-flat p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[var(--ff-primary)]">{icon}</span>
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--ff-text-secondary)] uppercase tracking-wider">{label}</span>
      <input
        type="text"
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        className="input-base w-full mt-1 text-sm"
        data-testid={testid}
      />
    </label>
  );
}
function NumberField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--ff-text-secondary)] uppercase tracking-wider">{label}</span>
      <input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="input-base w-full mt-1 text-sm tabular-nums"
        data-testid={testid}
      />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--ff-text-secondary)] uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-base w-full mt-1 text-sm"
        data-testid={testid}
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function ToggleField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2 sm:py-0">
      <span className="text-xs text-[var(--ff-text-secondary)] uppercase tracking-wider">{label}</span>
      <input
        type="checkbox"
        checked={!!value}
        onChange={e => onChange(e.target.checked)}
        className="h-5 w-5 accent-[var(--ff-primary)]"
        data-testid={testid}
      />
    </label>
  );
}
function ReadRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm gap-3">
      <span className="text-xs text-[var(--ff-text-secondary)] uppercase tracking-wider">{label}</span>
      <span className="text-[var(--ff-text-primary)] font-mono text-[11px] truncate max-w-[60%]">{value}</span>
    </div>
  );
}
function RecordList({ rows, empty, render }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-[var(--ff-text-muted)]">{empty}</p>;
  return (
    <ul className="divide-y divide-[var(--ff-border)]">
      {rows.map((r, i) => (
        <li key={r.id || i} className="py-3">{render(r)}</li>
      ))}
    </ul>
  );
}
